import React, { useState, useEffect, useMemo, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Star, Users, ThumbsUp, Search, Phone, ExternalLink, X, Clock } from 'lucide-react';
import BackButton from '../components/BackButton';
import BBALoader from '../components/BBALoader';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { db } from '../firebase.js';
import { collection, getDocs, getDoc, doc } from 'firebase/firestore';
import { getAccountById, findAccountIdByEmail } from '../services/firestoreService';
import {
  normEmail,
  pickProfilePicUrl,
  accountDisplayForCheckIn,
  checkInDateToJs,
  initialsFromName,
  CheckInStarTick,
  CheckInRatingPanel,
  CheckInPendingPanel,
} from '../components/crm/CheckInInsights';
import PortalBackground from '../components/PortalBackground';

// Resolve the selected timeframe to a [start, end) window; null means no bound on that side.
function timeRangeToWindow(timeRange, monthPick, customStart, customEnd) {
  const now = new Date();
  switch (timeRange) {
    case 'thisMonth': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      return { start, end };
    }
    case 'month': {
      const [y, m] = String(monthPick || '').split('-').map(Number);
      if (!y || !m) return null;
      return { start: new Date(y, m - 1, 1), end: new Date(y, m, 1) };
    }
    case 'last3Months':
    case 'last6Months':
    case 'lastYear': {
      const months = { last3Months: 3, last6Months: 6, lastYear: 12 }[timeRange];
      const start = new Date(now);
      start.setMonth(start.getMonth() - months);
      return { start, end: null };
    }
    case 'custom': {
      const start = customStart ? new Date(`${customStart}T00:00:00`) : null;
      // End date is inclusive, so bound at the start of the following day.
      const end = customEnd ? new Date(new Date(`${customEnd}T00:00:00`).getTime() + 24 * 60 * 60 * 1000) : null;
      if (!start && !end) return null;
      return { start, end };
    }
    default:
      return null; // allTime
  }
}

const currentMonthValue = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

// The "YYYY-MM" bucket a row belongs to, matching the dashboard's periodKey. Prefer the
// authored periodKey; otherwise derive from the date's UTC month (check-in scheduledDates are
// authored at UTC midnight, so local-time bucketing would shift a 1st-of-month send into the
// prior month).
function rowPeriodKey(r) {
  if (r.periodKey) return r.periodKey;
  if (!r.date) return null;
  return `${r.date.getUTCFullYear()}-${String(r.date.getUTCMonth() + 1).padStart(2, '0')}`;
}

// A check-in counts as "responded" once it carries a rating or a resolved status.
// Mirrors the shared definition in ClientOverview / the check-in cloud functions.
function isCheckInResponded(data) {
  const num = Number(data?.overallSatisfaction ?? data?.rating);
  const hasNumeric = Number.isFinite(num) && num >= 1 && num <= 5;
  const statusOk =
    typeof data?.overallStatus === 'string' &&
    ['Good', 'Okay', 'Not Good'].includes(data.overallStatus);
  return hasNumeric || data?.status === 'completed' || statusOk;
}

// Client avatar + company + contact, matching the dashboard hover panels.
function ClientCell({ row, size = 'h-10 w-10' }) {
  return (
    <div className="flex items-center gap-3 min-w-0">
      {row.imageUrl ? (
        <img src={row.imageUrl} alt="" className={`${size} rounded-full object-cover shrink-0 border border-gray-200 dark:border-gray-600`} />
      ) : (
        <div className={`${size} rounded-full shrink-0 flex items-center justify-center text-xs font-semibold bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-200`}>
          {initialsFromName(row.companyName)}
        </div>
      )}
      <div className="min-w-0">
        <div className="font-semibold text-gray-900 dark:text-white truncate">{row.companyName}</div>
        {row.contactName && row.contactName !== row.companyName ? (
          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{row.contactName}</div>
        ) : null}
      </div>
    </div>
  );
}

function StarRow({ rating, starClass = 'text-lg' }) {
  return (
    <div className="flex space-x-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <span
          key={star}
          className={`${starClass} ${rating >= star ? 'text-yellow-400' : 'text-gray-300 dark:text-gray-600'}`}
        >
          ★
        </span>
      ))}
    </div>
  );
}

const ClientSatisfaction = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [timeRange, setTimeRange] = useState('thisMonth');
  const [monthPick, setMonthPick] = useState(currentMonthValue);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [responseFilter, setResponseFilter] = useState('all'); // all | responded | noResponse
  const [searchQuery, setSearchQuery] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);

  // Hover popover for the rating bars (same behavior as the CRM dashboard card).
  const [chartHover, setChartHover] = useState(null); // { row }
  const chartHoverTimer = useRef(null);
  const openChartHover = (row) => {
    if (chartHoverTimer.current) clearTimeout(chartHoverTimer.current);
    setChartHover({ row });
  };
  const scheduleCloseChartHover = () => {
    if (chartHoverTimer.current) clearTimeout(chartHoverTimer.current);
    chartHoverTimer.current = setTimeout(() => setChartHover(null), 250);
  };

  // Hover popover for the "No Response" tile — lists clients who haven't responded.
  const [noResponseHoverOpen, setNoResponseHoverOpen] = useState(false);
  const noResponseHoverTimer = useRef(null);
  const openNoResponseHover = () => {
    if (noResponseHoverTimer.current) clearTimeout(noResponseHoverTimer.current);
    setNoResponseHoverOpen(true);
  };
  const scheduleCloseNoResponseHover = () => {
    if (noResponseHoverTimer.current) clearTimeout(noResponseHoverTimer.current);
    noResponseHoverTimer.current = setTimeout(() => setNoResponseHoverOpen(false), 250);
  };

  useEffect(() => () => {
    if (chartHoverTimer.current) clearTimeout(chartHoverTimer.current);
    if (noResponseHoverTimer.current) clearTimeout(noResponseHoverTimer.current);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      setLoading(true);
      try {
        // Survey responses live in surveyResults; check_ins carries the account link
        // (accountId / userEmail) plus pending state; scheduled_check_ins holds legacy /
        // orphaned outstanding sends that never got a check_ins doc. Merge all three so
        // the report shows every response AND everyone who hasn't gotten to it yet.
        const [resultsSnap, checkInsSnap, scheduledSnap] = await Promise.all([
          getDocs(collection(db, 'surveyResults')),
          getDocs(collection(db, 'check_ins')),
          getDocs(collection(db, 'scheduled_check_ins')),
        ]);

        const rowsByKey = new Map();
        // Track which check_ins docs we've seen so scheduled_check_ins synthesis can
        // skip any outstanding send that already has a check_ins row.
        const seenCheckInIds = new Set();

        for (const d of resultsSnap.docs) {
          const data = d.data();
          const num = Number(data.responses?.overall?.rating);
          const row = {
            id: d.id,
            userId: data.userId || null,
            surveyId: data.surveyId || null,
            rating: Number.isFinite(num) && num >= 1 && num <= 5 ? num : null,
            feedback: String(data.responses?.additionalFeedback || '').trim(),
            requestedCall: false,
            responded: true,
            accountId: null,
            userEmail: '',
            periodKey: null,
            date: checkInDateToJs(data.submittedAt),
          };
          const key = row.surveyId && row.userId ? `${row.surveyId}|${row.userId}` : `sr-${d.id}`;
          rowsByKey.set(key, row);
        }

        for (const d of checkInsSnap.docs) {
          const data = d.data();
          seenCheckInIds.add(d.id);
          const num = Number(data.overallSatisfaction ?? data.rating);
          const hasNumeric = Number.isFinite(num) && num >= 1 && num <= 5;
          // Every check_ins doc is either a response or an outstanding send — show them all.
          const isResponded = isCheckInResponded(data);

          const patch = {
            userId: data.userId || null,
            surveyId: data.surveyId || null,
            rating: hasNumeric ? num : null,
            feedback: String(data.comments || data.additionalFeedback || '').trim(),
            requestedCall: data.requestedCheckInCall === true,
            responded: isResponded,
            accountId: data.accountId || null,
            userEmail: String(data.userEmail || '').trim(),
            periodKey: data.periodKey || null,
            date: isResponded
              ? checkInDateToJs(data.submittedAt || data.completedAt || data.createdAt)
              : checkInDateToJs(data.scheduledDate || data.createdAt),
          };
          const key = patch.surveyId && patch.userId ? `${patch.surveyId}|${patch.userId}` : `ci-${d.id}`;
          const existing = rowsByKey.get(key);
          if (existing) {
            rowsByKey.set(key, {
              ...existing,
              rating: existing.rating ?? patch.rating,
              feedback: existing.feedback || patch.feedback,
              requestedCall: existing.requestedCall || patch.requestedCall,
              responded: existing.responded || patch.responded,
              accountId: existing.accountId || patch.accountId,
              userEmail: existing.userEmail || patch.userEmail,
              periodKey: existing.periodKey || patch.periodKey,
              date: existing.date || patch.date,
            });
          } else {
            rowsByKey.set(key, { id: d.id, ...patch });
          }
        }

        // Outstanding sends that live only in scheduled_check_ins (legacy rows, or ones
        // whose check_ins doc was removed). Skip any still-scheduled/cancelled ones and
        // any whose check_ins doc we already loaded above.
        for (const d of scheduledSnap.docs) {
          const data = d.data();
          if (data.status && data.status !== 'sent') continue;
          const checkInId = data.checkInId;
          if (checkInId && seenCheckInIds.has(checkInId)) continue;
          const key = checkInId ? `ci-${checkInId}` : `sched-${d.id}`;
          if (rowsByKey.has(key)) continue;
          const when = checkInDateToJs(data.scheduledDate || data.sentAt || data.createdAt);
          rowsByKey.set(key, {
            id: key,
            userId: null,
            surveyId: data.surveyId || null,
            rating: null,
            feedback: '',
            requestedCall: false,
            responded: false,
            accountId: data.clientId || null,
            userEmail: String(data.clientEmail || '').trim(),
            periodKey: data.periodKey || null,
            date: when,
          });
        }

        // Resolve each response to an account so we can show company, contact, and photo.
        const accountCache = new Map();
        const loadAccount = (id) => {
          if (!id) return Promise.resolve(null);
          if (!accountCache.has(id)) {
            accountCache.set(id, getAccountById(id).catch(() => null));
          }
          return accountCache.get(id);
        };
        const userCache = new Map();
        const loadUser = (uid) => {
          if (!uid) return Promise.resolve(null);
          if (!userCache.has(uid)) {
            userCache.set(
              uid,
              getDoc(doc(db, 'users', uid))
                .then((snap) => (snap.exists() ? snap.data() : null))
                .catch(() => null)
            );
          }
          return userCache.get(uid);
        };
        const emailAccountCache = new Map();
        const findAccountByEmailCached = (email) => {
          const n = normEmail(email);
          if (!n) return Promise.resolve(null);
          if (!emailAccountCache.has(n)) {
            emailAccountCache.set(n, findAccountIdByEmail(email).catch(() => null));
          }
          return emailAccountCache.get(n);
        };

        const resolved = await Promise.all(
          [...rowsByKey.values()].map(async (row) => {
            let account = null;
            let email = row.userEmail;
            let userData = null;

            if (row.accountId) account = await loadAccount(row.accountId);
            if (!account && row.userId) {
              userData = await loadUser(row.userId);
              if (userData?.accountId) account = await loadAccount(userData.accountId);
              if (!email && userData?.email) email = String(userData.email).trim();
            }
            if (!account && email) {
              const accId = await findAccountByEmailCached(email);
              if (accId) account = await loadAccount(accId);
            }
            // Legacy rows sometimes stored the account id in userId directly.
            if (!account && row.userId) account = await loadAccount(row.userId);

            if (account) {
              const disp = accountDisplayForCheckIn(account, email);
              return {
                ...row,
                companyName: disp.name,
                contactName: disp.contactName,
                imageUrl: disp.imageUrl,
                accountId: account.id,
              };
            }
            const userName = [userData?.firstName, userData?.lastName].filter(Boolean).join(' ').trim() || userData?.name || '';
            return {
              ...row,
              companyName: userData?.company || userName || email || 'Unknown Company',
              contactName: userName,
              imageUrl: pickProfilePicUrl(userData),
              accountId: null,
            };
          })
        );

        resolved.sort((a, b) => (b.date?.getTime() || 0) - (a.date?.getTime() || 0));
        if (!cancelled) setRows(resolved);
      } catch (error) {
        console.error('Error loading survey data:', error);
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadData();
    return () => {
      cancelled = true;
    };
  }, []);

  // Apply the timeframe + response-status + search filters to every card on the page.
  const filteredRows = useMemo(() => {
    let list = rows;
    // Calendar-month presets bucket by the check-in's periodKey (matching the CRM dashboard);
    // rolling/custom windows filter by the actual date.
    if (timeRange === 'thisMonth' || timeRange === 'month') {
      const target = timeRange === 'thisMonth' ? currentMonthValue() : monthPick;
      list = target ? list.filter((r) => rowPeriodKey(r) === target) : list;
    } else {
      const window = timeRangeToWindow(timeRange, monthPick, customStart, customEnd);
      if (window) {
        list = list.filter((r) => {
          if (!r.date) return false;
          const t = r.date.getTime();
          if (window.start && t < window.start.getTime()) return false;
          if (window.end && t >= window.end.getTime()) return false;
          return true;
        });
      }
    }
    if (responseFilter === 'responded') list = list.filter((r) => r.responded);
    if (responseFilter === 'noResponse') list = list.filter((r) => !r.responded);
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter((r) =>
        [r.companyName, r.contactName, r.userEmail, r.feedback]
          .some((v) => String(v || '').toLowerCase().includes(q))
      );
    }
    return list;
  }, [rows, timeRange, monthPick, customStart, customEnd, responseFilter, searchQuery]);

  const metrics = useMemo(() => {
    const responded = filteredRows.filter((r) => r.responded);
    const rated = responded.filter((r) => r.rating != null);
    const avg = rated.length ? rated.reduce((sum, r) => sum + r.rating, 0) / rated.length : null;
    const happy = rated.filter((r) => r.rating >= 4).length;
    return {
      avg,
      totalResponses: responded.length,
      noResponse: filteredRows.length - responded.length,
      happyPct: rated.length ? Math.round((happy / rated.length) * 100) : null,
      ratedCount: rated.length,
    };
  }, [filteredRows]);

  // Star distribution for the bar chart, with submitter details for the hover panel.
  const barChartRows = useMemo(() => {
    const buckets = [0, 1, 2, 3, 4].map((i) => ({ stars: `${i + 1}`, count: 0, submitters: [] }));
    for (const r of filteredRows) {
      if (r.rating == null) continue;
      const idx = Math.min(4, Math.max(0, Math.round(r.rating) - 1));
      buckets[idx].count += 1;
      buckets[idx].submitters.push({
        name: r.companyName,
        contactName: r.contactName,
        imageUrl: r.imageUrl,
        accountId: r.accountId,
        comment: r.feedback,
        requestedCall: r.requestedCall,
        date: r.date,
      });
    }
    return buckets;
  }, [filteredRows]);

  // Clients in the current window who haven't responded — powers the No Response hover panel.
  const noResponseSubmitters = useMemo(
    () =>
      filteredRows
        .filter((r) => !r.responded)
        .map((r) => ({
          name: r.companyName,
          contactName: r.contactName,
          imageUrl: r.imageUrl,
          accountId: r.accountId,
          date: r.date,
        })),
    [filteredRows]
  );

  const overviewTiles = [
    {
      label: 'Overall Score',
      value: metrics.avg != null ? `${metrics.avg.toFixed(1)}/5` : '—',
      icon: Star,
      iconClass: 'text-amber-500 bg-amber-100 dark:bg-amber-900/40',
    },
    {
      label: 'Total Responses',
      value: metrics.totalResponses,
      icon: Users,
      iconClass: 'text-blue-500 bg-blue-100 dark:bg-blue-900/40',
    },
    {
      id: 'noResponse',
      label: 'Outstanding',
      value: metrics.noResponse,
      icon: Clock,
      iconClass: 'text-amber-500 bg-amber-100 dark:bg-amber-900/40',
    },
    {
      label: 'Happy Clients (4★+)',
      value: metrics.happyPct != null ? `${metrics.happyPct}%` : '—',
      icon: ThumbsUp,
      iconClass: 'text-green-600 bg-green-100 dark:bg-green-900/40',
    },
  ];

  return (
    <div className="min-h-screen flex flex-col w-full p-4 relative z-50">
      <PortalBackground />
      <div className="relative">
      <div className="flex items-center justify-between mb-6">
        <BackButton onClick={() => navigate('/crm/reports')} />
        <h1 className={`text-3xl font-bold tracking-tight text-gray-900 dark:text-white flex-1 text-center`}>Client Check Ins</h1>
        <div className="w-[100px]"></div>
      </div>

      {/* Search + timeframe + response filters (apply to everything below) */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[220px]">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search company, contact, or feedback..."
            className={`w-full pl-9 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${
              darkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 placeholder-gray-400'
            }`}
          />
        </div>
        <select
          className={`px-4 py-2 ${darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300'} border rounded-md shadow-sm`}
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
        >
          <option value="thisMonth">This Month</option>
          <option value="month">Pick a Month</option>
          <option value="custom">Date Range</option>
          <option value="last3Months">Last 3 Months</option>
          <option value="last6Months">Last 6 Months</option>
          <option value="lastYear">Last Year</option>
          <option value="allTime">All Time</option>
        </select>
        {timeRange === 'month' && (
          <input
            type="month"
            value={monthPick}
            onChange={(e) => setMonthPick(e.target.value)}
            className={`px-3 py-2 border rounded-md shadow-sm ${
              darkMode ? 'bg-gray-800 border-gray-600 text-white [color-scheme:dark]' : 'bg-white border-gray-300'
            }`}
          />
        )}
        {timeRange === 'custom' && (
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              className={`px-3 py-2 border rounded-md shadow-sm ${
                darkMode ? 'bg-gray-800 border-gray-600 text-white [color-scheme:dark]' : 'bg-white border-gray-300'
              }`}
            />
            <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>to</span>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              className={`px-3 py-2 border rounded-md shadow-sm ${
                darkMode ? 'bg-gray-800 border-gray-600 text-white [color-scheme:dark]' : 'bg-white border-gray-300'
              }`}
            />
          </div>
        )}
        <select
          className={`px-4 py-2 ${darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300'} border rounded-md shadow-sm`}
          value={responseFilter}
          onChange={(e) => setResponseFilter(e.target.value)}
        >
          <option value="all">All Statuses</option>
          <option value="responded">Responded</option>
          <option value="noResponse">Outstanding</option>
        </select>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center w-full min-h-[300px]">
          <BBALoader size={48} className="mb-4" />
          <p className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading satisfaction data...</p>
        </div>
      ) : (
        <>
          {/* Overview: KPI tiles + rating distribution chart */}
          <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 mb-6`}>
            <h2 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>Overview</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {overviewTiles.map((tile) => {
                const isNoResponse = tile.id === 'noResponse';
                const interactive = isNoResponse && metrics.noResponse > 0;
                return (
                  <div
                    key={tile.label}
                    className={`relative rounded-lg ${darkMode ? 'bg-gray-700/60' : 'bg-gray-50'} p-4 flex flex-col items-center text-center ${
                      interactive ? 'cursor-pointer hover:ring-2 hover:ring-amber-300 dark:hover:ring-amber-500/40 transition' : ''
                    }`}
                    onMouseEnter={interactive ? openNoResponseHover : undefined}
                    onMouseLeave={interactive ? scheduleCloseNoResponseHover : undefined}
                    onClick={interactive ? () => setResponseFilter('noResponse') : undefined}
                  >
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center mb-2 ${tile.iconClass}`}>
                      <tile.icon className="w-5 h-5" />
                    </div>
                    <span className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>{tile.value}</span>
                    <span className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{tile.label}</span>
                    {interactive && noResponseHoverOpen ? (
                      <div
                        className="absolute z-50 left-1/2 -translate-x-1/2 top-full mt-2 text-left"
                        onMouseEnter={() => { if (noResponseHoverTimer.current) clearTimeout(noResponseHoverTimer.current); }}
                        onMouseLeave={scheduleCloseNoResponseHover}
                      >
                        <CheckInPendingPanel submitters={noResponseSubmitters} darkMode={darkMode} />
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>

            {metrics.ratedCount === 0 ? (
              <p className={`text-center text-sm py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                No ratings for this timeframe yet.
              </p>
            ) : (
              <div className="relative w-full h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={barChartRows.filter((r) => r.count > 0)}
                    margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                    onMouseMove={(state) => {
                      if (state?.isTooltipActive && state.activePayload?.length) {
                        openChartHover(state.activePayload[0].payload);
                      }
                    }}
                    onMouseLeave={scheduleCloseChartHover}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
                    <XAxis
                      dataKey="stars"
                      interval={0}
                      height={44}
                      tick={(props) => <CheckInStarTick {...props} darkMode={darkMode} />}
                    />
                    <YAxis allowDecimals={false} tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }} />
                    <Tooltip
                      content={() => null}
                      cursor={{ fill: darkMode ? 'rgba(55, 65, 81, 0.15)' : 'rgba(0,0,0,0.06)' }}
                    />
                    <Bar dataKey="count" fill="#f59e0b" radius={[4, 4, 0, 0]} name="Responses" />
                  </BarChart>
                </ResponsiveContainer>

                {chartHover?.row ? (
                  <div
                    className="absolute z-50 left-1/2 -translate-x-1/2"
                    style={{ top: -150 }}
                    onMouseEnter={() => {
                      if (chartHoverTimer.current) clearTimeout(chartHoverTimer.current);
                    }}
                    onMouseLeave={scheduleCloseChartHover}
                  >
                    <CheckInRatingPanel row={chartHover.row} darkMode={darkMode} />
                  </div>
                ) : null}
              </div>
            )}
          </div>

          {/* Survey Responses Table */}
          <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 mb-6`}>
            <h2 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              Survey Responses
              <span className={`ml-2 text-sm font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                ({filteredRows.length})
              </span>
            </h2>
            {filteredRows.length === 0 ? (
              <p className={`text-center py-8 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {rows.length === 0
                  ? 'No survey results yet. Send a survey to start collecting feedback!'
                  : 'Nothing matches the current filters.'}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <th className={`text-left py-3 px-4 font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Company</th>
                      <th className={`text-left py-3 px-4 font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Date</th>
                      <th className={`text-left py-3 px-4 font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Score</th>
                      <th className={`text-left py-3 px-4 font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Phone Call</th>
                      <th className={`text-left py-3 px-4 font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Additional Feedback</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((row) => (
                      <tr
                        key={row.id}
                        onClick={() => setSelectedRow(row)}
                        className={`border-b cursor-pointer transition-colors ${
                          darkMode ? 'border-gray-700 hover:bg-gray-700/60' : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <td className="py-3 px-4">
                          <ClientCell row={row} />
                        </td>
                        <td className={`py-3 px-4 whitespace-nowrap ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          {row.date ? row.date.toLocaleDateString() : 'Unknown date'}
                        </td>
                        <td className={`py-3 px-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {row.rating != null ? (
                            <div className="flex items-center space-x-1">
                              <span className="font-semibold">{row.rating}/5</span>
                              <StarRow rating={row.rating} />
                            </div>
                          ) : !row.responded ? (
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                              darkMode ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-100 text-amber-700'
                            }`}>
                              <Clock className="w-3.5 h-3.5" />
                              Outstanding
                            </span>
                          ) : (
                            <span className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>—</span>
                          )}
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap">
                          {row.requestedCall ? (
                            <span className={`inline-flex items-center gap-1.5 font-semibold ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                              <Phone className="w-4 h-4" />
                              Yes
                            </span>
                          ) : (
                            <span className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No</span>
                          )}
                        </td>
                        <td className={`py-3 px-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          <span className="line-clamp-2">{row.feedback || '-'}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Response Detail Modal */}
      {selectedRow && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedRow(null)}
        >
          <div
            className={`bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-md w-full relative`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedRow(null)}
              className={`absolute top-4 right-4 p-1 rounded ${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex flex-col items-center text-center">
              {selectedRow.imageUrl ? (
                <img
                  src={selectedRow.imageUrl}
                  alt=""
                  className="h-16 w-16 rounded-full object-cover border border-gray-200 dark:border-gray-600 mb-3"
                />
              ) : (
                <div className="h-16 w-16 rounded-full flex items-center justify-center text-lg font-semibold bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-200 mb-3">
                  {initialsFromName(selectedRow.companyName)}
                </div>
              )}
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>{selectedRow.companyName}</h3>
              {selectedRow.contactName && selectedRow.contactName !== selectedRow.companyName ? (
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{selectedRow.contactName}</p>
              ) : null}
              <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {selectedRow.date ? selectedRow.date.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }) : 'Unknown date'}
              </p>

              {selectedRow.rating != null ? (
                <div className="flex items-center gap-2 mt-3">
                  <span className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>{selectedRow.rating}/5</span>
                  <StarRow rating={selectedRow.rating} starClass="text-2xl" />
                </div>
              ) : !selectedRow.responded ? (
                <span className={`inline-flex items-center gap-1.5 mt-3 px-3 py-1 rounded-full text-xs font-medium ${
                  darkMode ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-100 text-amber-700'
                }`}>
                  <Clock className="w-3.5 h-3.5" />
                  Hasn't responded yet
                </span>
              ) : null}

              {selectedRow.feedback ? (
                <p className={`text-sm italic mt-4 break-words whitespace-pre-wrap ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  "{selectedRow.feedback}"
                </p>
              ) : null}

              {selectedRow.requestedCall ? (
                <span className={`inline-flex items-center gap-1.5 mt-3 px-3 py-1 rounded-full text-xs font-medium ${
                  darkMode ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-100 text-amber-700'
                }`}>
                  <Phone className="w-3.5 h-3.5" />
                  Requested a check-in call
                </span>
              ) : null}

              {selectedRow.accountId ? (
                <button
                  type="button"
                  onClick={() => navigate(`/crm/client/${selectedRow.accountId}`)}
                  className="mt-5 w-full py-2 px-3 rounded-lg text-sm font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors inline-flex items-center justify-center gap-1.5"
                >
                  View Client
                  <ExternalLink className="w-3.5 h-3.5" />
                </button>
              ) : null}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default ClientSatisfaction;
